/**
 * The deal's name, editable in place — the Name row of the sidebar's Deal block,
 * on every brand.
 *
 * What it shows is the EFFECTIVE name: a rep's name when one has been set,
 * otherwise the linked Pipedrive deal's title, otherwise the records' name. The
 * rep's name is HickoryOS's own value — it survives every later sync and is
 * never written to Pipedrive, and on a deal holding a Pipedrive deal the row
 * says so rather than refusing the edit.
 *
 * The records' name never disappears. When it differs from the rep's, one line
 * names it and offers it back in a click — which CLEARS the rep's name rather
 * than copying the records' over it, so later renames at the source keep
 * flowing through. A rep's name stands until a rep changes it.
 *
 * On an enforced brand with a linked project and ServiceTitan writes on, the
 * save also renames the ServiceTitan project; until that lands the row says so.
 * Everywhere else the save stays in HickoryOS and the copy never claims a
 * ServiceTitan rename.
 */

import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PRESS_ICON } from "@/components/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePipedriveFrozen } from "@/hooks/use-brands";
import { useCapabilities } from "@/hooks/use-capabilities";
import { formatBusinessDate } from "@/lib/business-date";
import { htmlToText } from "@/lib/sop-summary";
import { TAP_AREA } from "@/lib/tap-area";
import { cn } from "@/lib/utils";
import { canRenameDeal, useRenameDeal } from "@/hooks/use-deal-board";
import type { DealDetail } from "@/hooks/use-deal-board";

export function DealTitleEditor({ deal }: { deal: DealDetail }) {
  const { can } = useCapabilities();
  const editable = can("deal_management.triage") && canRenameDeal(deal);
  const rename = useRenameDeal();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  // A Pipedrive title can arrive as markup: edit and show its text, never the code.
  // The effective name, as the headline and the card print it. `title_pending` only
  // drives the ServiceTitan progress note: after a reset it can still name the
  // discarded rename, and the row must never disagree with the headline.
  const shown = htmlToText(deal.title);
  // The pending name and the records' one (which follows the project) differ
  // while ServiceTitan is catching up.
  // Only while the rep's CURRENT name is the one on its way: after a reset the queued
  // write may still name the discarded rename, and that is no longer this deal's name.
  const syncing =
    deal.title_source === "operator" &&
    deal.title_pending != null &&
    htmlToText(deal.title_pending) === htmlToText(deal.title) &&
    deal.title_pending !== deal.system_title;
  // Where the save also renames the ServiceTitan project — the only place the
  // copy may say so.
  const renamesProject =
    deal.stage_enforced && deal.st_project_id != null && (deal.st_create?.enabled ?? false);
  const repSet = deal.title_source === "operator";
  const systemTitle = htmlToText(deal.system_title);
  // Quote the records' name only when it says something the rep's does not.
  const recordsDiffer = systemTitle !== "" && systemTitle !== htmlToText(deal.title);
  // Always while a rep's name holds: once a ServiceTitan rename lands the records say the
  // same thing, yet the rep's name still masks every later change, so the way back stays.
  const showSystemHint = repSet;
  // `system_title` can come from Pipedrive even on an enforced brand with a project,
  // so the hint names no single source.
  const systemSource = "The records say";
  const pending = rename.isPending;
  // The Pipedrive line names Pipedrive, so it waits for the brand to be KNOWN to
  // still be on it: never on a frozen brand, never in the cold-load window.
  const onPipedrive = usePipedriveFrozen(deal.tenant_id) === false;

  const save = async (title: string | null) => {
    try {
      // `mutateAsync` rather than `mutate` with callbacks: TanStack drops a
      // superseded call's callbacks, and closing the editor is exactly the
      // callback a rapid second save would lose.
      await rename.mutateAsync({ dealId: deal.id, title });
      setEditing(false);
      setError(null);
    } catch (e) {
      // The server's 409 is a sentence written for the rep; quote it rather
      // than a generic line.
      toast.error(e instanceof Error && e.message ? e.message : "Couldn't rename the deal.");
    }
  };

  const startEditing = () => {
    setDraft(shown);
    setError(null);
    setEditing(true);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) {
      setError("Enter a name.");
      return;
    }
    // Every surface reads a deal's name as possible Pipedrive markup, so `<…>` would vanish.
    if (/[<>]/.test(trimmed)) {
      setError("Names can't contain < or >.");
      return;
    }
    if (trimmed === shown) {
      setEditing(false);
      return;
    }
    void save(trimmed);
  };

  return (
    <div className="min-w-0 space-y-0.5">
      {editing ? (
        <form onSubmit={submit} className="flex items-center gap-1" aria-busy={pending}>
          <Input
            size="sm"
            aria-label="Deal name"
            aria-invalid={error ? true : undefined}
            autoFocus
            maxLength={255}
            value={draft}
            disabled={pending}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setEditing(false);
            }}
            className="h-11 md:h-7 w-full"
          />
          <Button
            type="submit"
            size="icon"
            {...PRESS_ICON}
            variant="ghost"
            className="size-11 md:size-7"
            aria-label={pending ? "Saving name" : "Save name"}
            disabled={pending}
          >
            <Check className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button
            type="button"
            size="icon"
            {...PRESS_ICON}
            variant="ghost"
            className="size-11 md:size-7"
            aria-label="Cancel editing name"
            disabled={pending}
            onClick={() => setEditing(false)}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </form>
      ) : (
        <div className="flex items-center gap-1.5">
          {shown ? (
            <span className="min-w-0 break-words font-medium">{shown}</span>
          ) : (
            <span className="text-muted-foreground">Unnamed</span>
          )}
          {editable ? (
            <button
              type="button"
              aria-label="Edit name"
              onClick={startEditing}
              className={cn("relative shrink-0 cursor-pointer text-muted-foreground hover:text-foreground", TAP_AREA)}
            >
              <Pencil className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </div>
      )}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      {repSet && deal.title_set_at ? (
        <p className="text-xs text-muted-foreground">
          Set by {deal.title_set_by_name ?? "a rep"} on {formatBusinessDate(deal.title_set_at)}
        </p>
      ) : null}
      {showSystemHint ? (
        <p className="break-words text-xs text-muted-foreground">
          {recordsDiffer ? `${systemSource} “${systemTitle}”` : "Named here"}
          {editable ? (
            <>
              {" — "}
              <button
                type="button"
                onClick={() => void save(null)}
                disabled={pending}
                className="cursor-pointer font-medium text-primary hover:underline disabled:opacity-50"
              >
                {recordsDiffer ? "Use it" : "Follow the records"}
              </button>
            </>
          ) : null}
        </p>
      ) : null}
      {syncing ? (
        <p className="text-xs text-muted-foreground">
          Renaming the ServiceTitan project too — it follows on its next sync, usually within half an hour.
        </p>
      ) : editable && editing && renamesProject ? (
        <p className="text-xs text-muted-foreground">This also renames the ServiceTitan project.</p>
      ) : null}
      {deal.has_pipedrive_deal && onPipedrive && editable && (editing || repSet) ? (
        <p className="text-xs text-muted-foreground">Changes here stay in HickoryOS and do not sync to Pipedrive.</p>
      ) : null}
    </div>
  );
}
